'use client';

import React, { useMemo, useState, useRef } from 'react';
import styles from './calendar.module.css';
import { EventItem } from '@/app/interfaces/event';
import { motion } from 'motion/react';
import { containerVariants } from '../../utils/animationVariants/posterContainer';
import { useRouter } from 'next/navigation';
import { Secretariat } from '@/app/interfaces/secretariat';
import { useLocale, useTranslations } from 'next-intl';
import { getSecretariatColor } from '@/app/utils/secretariatColorDetection';
import Modal from '@/components/modal/modal';


// --- TYPES ---

type CalendarProps = {
  events: EventItem[];
};


type DayCell = {
  dayNumber: number;
  formattedDate: string;
  isToday: boolean;
  isOtherMonth: boolean;
  event?: EventItem;
};

// Shared arrays for days and months


// Reusable helper to format dates from objects
const formatDate = (date: Date): string => {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
};

// Reusable helper for displaying human-readable dates from strings
const formatReadableDate = (dateString: string, monthNames: string[]): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// --- SUB-COMPONENT: EVENTOVERLAY ---
// Created a small private sub-component for the overlay based on image_12.png
type EventOverlayProps = {
  event: EventItem;
  monthNames: string[];
  isHoverMode?: boolean;
};

const EventOverlay = ({ event, monthNames, isHoverMode }: EventOverlayProps) => {
  const router = useRouter();
  const timeRange = `${event.startTime?.substring(0, 5)} - ${event.endTime?.substring(0, 5)}`;
  const dateFormatted = formatReadableDate(event.date, monthNames);
  const t = useTranslations("EventPage")

  return (
    <div className={isHoverMode ? styles.desktopHoverCard : styles.overlayContainer}>
      <h3 className={styles.overlayTitle}>{event.title}</h3>
      <div className={styles.overlayDetails}>
        <span>{dateFormatted}</span>
        <span>{timeRange}</span>
      </div>
      <button className={styles.detailButton} onClick={() => { router.push(`/events/${event.id}`) }}>
        {t('viewDetail')}
      </button>
    </div>
  );
};

// --- MAIN COMPONENT: CALENDAR ---
export default function Calendar({ events }: CalendarProps) {
  const locale = useLocale();
  const t = useTranslations('EventPage');
  const isRTL = locale === 'ar';

  // Compute translations internally
  const daysOfWeek = useMemo(() => [
    t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')
  ], [t]);

  const monthNames = useMemo(() => [
    t('jan'), t('feb'), t('mar'), t('apr'), t('may'), t('jun'),
    t('jul'), t('aug'), t('sept'), t('oct'), t('nov'), t('dec')
  ], [t]);

  const today = t('today');

  const secretaries: Secretariat[] = useMemo(() => [
    { name: t('academic'), id: '1' },
    { name: t('women'), id: '2' },
    { name: t('social'), id: '3' },
    { name: t('sport'), id: '4' }
  ], [t]);

  // Use a Date object to track the first day of the displayed month
  const todayDate = new Date();
  const [currentMonthDate, setCurrentMonthDate] = useState(
    new Date(todayDate.getFullYear(), todayDate.getMonth(), 1)
  );

  // Derived date variables for ease of use
  const year = currentMonthDate.getFullYear();
  const month = currentMonthDate.getMonth();
  const monthName = monthNames[month];

  // State to track which day's event overlay to show
  const [hoveredEvent, setHoveredEvent] = useState<EventItem | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const overlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // --- NAVIGATION FUNCTIONS ---
  const handlePrevMonth = () => {
    setCurrentMonthDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonthDate(new Date(year, month + 1, 1));
  };

  const handleTodayClick = () => {
    setCurrentMonthDate(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
  };

  // --- DERIVED DATA & LOGIC ---

  // 1. Convert events array to a key-value map for quick day lookup
  const eventsByDayMap = useMemo(() => {
    const map: { [key: string]: EventItem } = {};
    events.forEach(event => {
      map[event.date] = event; // Will keep the last event per day if multiple exist
    });
    return map;
  }, [events]);

  // 2. Generate the flat array of day cells to be mapped into the grid
  const calendarCells = useMemo(() => {
    const todayStr = formatDate(todayDate);
    const cells: DayCell[] = [];

    // Calculate number of padding days from the end of the previous month
    const firstDayIndex = new Date(year, month, 1).getDay(); // Index 0-6 (Sun-Sat)
    const prevMonthDays = new Date(year, month, 0).getDate();

    // Fill previous month days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const dateDate = new Date(year, month - 1, day);
      const formattedDate = formatDate(dateDate);

      cells.push({
        dayNumber: day,
        formattedDate: formattedDate,
        isToday: false, // Prev month day can't be today
        isOtherMonth: true,
        event: eventsByDayMap[formattedDate],
      });
    }

    // Calculate number of days in the current month
    const numDaysInMonth = new Date(year, month + 1, 0).getDate();

    // Fill current month days
    for (let i = 1; i <= numDaysInMonth; i++) {
      const dateDate = new Date(year, month, i);
      const formattedDate = formatDate(dateDate);
      const isToday = todayStr === formattedDate;

      cells.push({
        dayNumber: i,
        formattedDate: formattedDate,
        isToday: isToday,
        isOtherMonth: false,
        event: eventsByDayMap[formattedDate],
      });
    }

    // Note: Padding days at the end of the month are typically not shown,
    // so I have omitted them to keep the logic clean and match common views.
    // The grid should align the current month properly and show padding only at the start.

    return cells;
  }, [currentMonthDate, eventsByDayMap]);

  // Handle setting/clearing the hovered event for the overlay
  const handleCellHover = (cell: DayCell) => {
    if (cell.event && !cell.isOtherMonth) {
      setHoveredEvent(cell.event);
    }
  };



  return (
    <motion.div
      variants={containerVariants} initial="hidden" animate="show"
      className={styles.calendarContainer}>

      {/* 1. Header with navigation, title, today button, and simplified legend */}
      <div className={styles.header}>
        <div className={styles.controlsGroup}>
          <div className={styles.monthNav}>
            <button onClick={handlePrevMonth} className={styles.navButton}>
              {isRTL ? (
                // Right arrow for RTL "previous"
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m9 5 7 7-7 7" />
                </svg>
              ) : (
                // Left arrow for LTR "previous"
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m15 19-7-7 7-7" />
                </svg>
              )}
            </button>

            <h1 className={styles.monthTitle}>{`${monthName} ${year}`}</h1>

            <button onClick={handleNextMonth} className={styles.navButton}>
              {isRTL ? (
                // Left arrow for RTL "next"
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m15 19-7-7 7-7" />
                </svg>
              ) : (
                // Right arrow for LTR "next"
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m9 5 7 7-7 7" />
                </svg>
              )}
            </button>
          </div>

          <button onClick={handleTodayClick} type="button" className={styles.todayButton}>
            {today}
          </button>
        </div>

        {/* Simplified legend for key types from your images: Sport, Academic, Women's, Social */}
        <div className={styles.legend}>
          {secretaries.map((secretary, index) => (
            // will be adjusted later along with its logic
            <div className={styles.legendItem} key={index}>
              <span className={`${styles.dot}`} style={{ backgroundColor: getSecretariatColor(secretary.name) }}></span> {secretary.name}
            </div>
          ))}


          {/* <div className={styles.legendItem}>
            <span className={`${styles.dot} ${styles.socialTheme}`}></span> Social
          </div> */}
        </div>
      </div>

      {/* 2. Days of week row */}
      <div className={styles.daysOfWeekGrid}>
        {daysOfWeek.map((day, index) => (
          <div key={index} className={styles.dayOfWeek}>{day}</div>
        ))}
      </div>

      {/* 3. Main calendar day grid (implented using grid to form cells) */}
      <div className={styles.calendarBodyGrid}>
        {calendarCells.map((cell) => {
          const isSelectedDay = false;
          const eventColor = cell.event ? getSecretariatColor(cell.event.secretariat.name) : '';

          // Determine the day of the week (0 = Sunday, 6 = Saturday)
          const dayOfWeek = new Date(cell.formattedDate).getDay();

          // Assign a smart alignment class based on the column
          let overlayPositionClass = styles.overlayCenter;
          if (isRTL) {
            // In RTL, columns are visually flipped
            if (dayOfWeek === 5 || dayOfWeek === 6) {
              overlayPositionClass = styles.overlayLeftEdge;  // visually on the left in RTL
            } else if (dayOfWeek === 0 || dayOfWeek === 1) {
              overlayPositionClass = styles.overlayRightEdge; // visually on the right in RTL
            }
          } else {
            if (dayOfWeek === 0 || dayOfWeek === 1) {
              overlayPositionClass = styles.overlayLeftEdge;
            } else if (dayOfWeek === 5 || dayOfWeek === 6) {
              overlayPositionClass = styles.overlayRightEdge;
            }
          }

          return (
            <div
              key={cell.formattedDate}
              className={`${styles.dayCell} ${cell.isOtherMonth ? styles.dayCellOtherMonth : ''} ${cell.isToday ? styles.dayCellToday : ''}`}
              onMouseEnter={() => handleCellHover(cell)}
              onMouseLeave={(e) => {
                // Check if mouse is moving towards the overlay area, not the document entirely
                const relatedTarget = e.relatedTarget as Node | null;
                if (!relatedTarget) {
                  setHoveredEvent(null); // Really leaving the window
                }
                // Otherwise do nothing, let overlay onMouseEnter/OnMouseLeave handle transition
              }}
              onClick={() => {
                if (cell.event && !cell.isOtherMonth) {
                  setSelectedEvent(cell.event);
                }
              }}
            >
              <div className={styles.dayContent}>
                <span className={`${styles.dayNumber} ${cell.isToday ? styles.dayNumberToday : ''} ${isSelectedDay ? styles.dayNumberSelected : ''}`}>
                  {cell.dayNumber}
                </span>

                {cell.event && !cell.isOtherMonth && (
                  <span className={`${styles.dot} ${styles.eventDotIndicator} `} style={{ backgroundColor: eventColor }}></span>
                )}
              </div>

              {/* Apply the smart position class to the wrapper */}
              {hoveredEvent && hoveredEvent.id === cell.event?.id && !cell.isOtherMonth && (
                <div
                  className={`${styles.hoverOverlayWrapper} ${overlayPositionClass}`}
                  onMouseEnter={(e) => {
                    setHoveredEvent(hoveredEvent);
                    e.stopPropagation();
                  }}
                  onMouseLeave={() => {
                    setHoveredEvent(null);
                  }}
                >
                  {/* Add isHoverMode={true} right here: */}
                  <EventOverlay event={hoveredEvent} monthNames={monthNames} isHoverMode={true} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Use the new Reusable Modal */}
      <Modal
        isOpen={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
      >
        {/* We only render the EventOverlay if an event is actually selected */}
        {selectedEvent && (
          <EventOverlay event={selectedEvent} monthNames={monthNames} />
        )}
      </Modal>

    </motion.div>
  );
}